// src/utils/geoFormat.jsx

/**
 * Wrap a longitude into the canonical [-180, 180] range. Useful when a
 * Leaflet/projection longitude has been "unwrapped" past ±180 due to
 * antimeridian panning, and the displayed/transmitted value should stay
 * in the standard range.
 *
 * @param {number} lng
 * @returns {number}
 */
export function normaliseLng(lng) {
  if (!Number.isFinite(lng)) return lng
  return ((((lng + 180) % 360) + 360) % 360) - 180
}

/**
 * Convert decimal degree coordinates to degrees-minutes (DM) format.
 *
 * @param {[number, number]} param0 - A tuple [lon, lat] in decimal degrees.
 * @returns {[string, string]} Formatted latitude and longitude strings in DM.
 *
 * @example
 * ConvertDDToDM([10.1234, 53.5678])
 * // => ['53° 34.1\' N', '10° 7.4\' E']
 */
export function ConvertDDToDM([lon, lat]) {
  const fmt = (v, isLat) => {
    const sign = v < 0 ? -1 : 1
    const abs = Math.abs(v)
    const deg = Math.floor(abs)
    const min = (abs - deg) * 60
    const dir = isLat ? (sign < 0 ? "S" : "N") : sign < 0 ? "W" : "E"
    return `${deg}°${min.toFixed(1)}'${dir}`
  }
  return [fmt(lat, true), fmt(lon, false)]
}

/**
 * Format a (lng, lat) pair as "<lat-DM>, <lon-DM>", with the longitude
 * normalised to the canonical [-180, 180] range so we never show a wrapped
 * value like "246° W". Single source of truth for inputs and popups that
 * display a lat,lng pair as text.
 *
 * @param {number} lng - decimal-degrees longitude (may be unwrapped)
 * @param {number} lat - decimal-degrees latitude
 * @returns {string}
 */
export function formatLatLngDM(lng, lat) {
  const [latStr, lonStr] = ConvertDDToDM([normaliseLng(lng), lat])
  return `${latStr}, ${lonStr}`
}